// Запуск workflow: состояния, этапы, источники.
//
// Источник: design/README.md, State Management и экраны 04, 05, 06.
// Это ядро общего слоя — правила ниже не должны переписываться при
// добавлении платформы. Если при запуске iOS придётся тронуть этот файл,
// граница слоёв проведена неверно (проверка из SMY-99).

import type { ScenarioId } from './config.ts'
import { formatUnit, type UnitId } from './units.ts'

// Подтверждение списания переехало в confirmation.ts: это не про ход запуска,
// а про то, запускать ли его вообще, и правил там набралось на файл.

export const RUN_STATUSES = ['idle', 'confirming', 'running', 'done', 'cancelled'] as const
export type RunStatus = (typeof RUN_STATUSES)[number]

/**
 * Состояния этапа.
 *
 * `skipped` появился 06.09.2026 вместе с веб-поиском внутри ответа. Список
 *  этапов объявляется ДО первого из них, а нужен ли поход в интернет,
 *  известно только после классификации — то есть после первого этапа. Значит,
 *  этап обязан уметь не состояться, и это должно быть видно как отдельное
 *  состояние: `done` соврал бы про сделанную работу, `failed` — про поломку.
 *
 *  Показывать его не надо: экран 05 обещает доказательство работы, а
 *  несостоявшийся этап работой не является. Но в снимке запуска он есть —
 *  иначе список этапов посреди прогона менял бы длину.
 */
export const STEP_STATUSES = ['pending', 'active', 'done', 'failed', 'skipped'] as const
export type StepStatus = (typeof STEP_STATUSES)[number]

export interface RunStep {
  id: string
  /** Формулировка для экрана 05 — на языке пользователя, без названий моделей. */
  label: string
  /**
   * Формулировка для уже выполненного этапа.
   *
   * Экран 05 называет сделанное в прошедшем времени («Нашёл источники»), а
   * идущее и предстоящее — в настоящем («Сверяю расхождения»). Одной строкой
   * это не выражается, поэтому их две; нет второй — годится первая.
   */
  doneLabel?: string
  status: StepStatus
  /**
   * Уточнение к ярлыку, появляющееся по ходу этапа: «3 утверждения»,
   * «6 независимых источников». Экран 05 обещает не полосу загрузки, а
   * доказательство работы, и вот это и есть доказательство.
   */
  detail?: string
}

export interface Source {
  title: string
  url: string
  /** Дата публикации источника: обещание «актуальные ссылки, дата» с экрана 02. */
  publishedAt?: string
}

/**
 * Мягкое предложение дорогого сценария в свободном чате. SMY-46
 *
 * Появляется РЯДОМ с обычным ответом, а не вместо него. Карточки задач не
 * заменяют чат, а дорогая задача предлагается, но не навязывается: экрана 04
 * здесь нет и списывать нечего — пользователь ничего не запускал.
 */
export interface ScenarioOffer {
  scenario: ScenarioId
  /** Чем спишется, если пользователь согласится. */
  unit: UnitId
  /** Готовая формулировка для ленты: «Похоже, это проверка фактов…». */
  text: string
}

/**
 * Граница ответственности рядом с ответом. SMY-77 · P7 Э7.1
 *
 * Живёт в ядре, а не в API, по той же причине, что и `ScenarioOffer`: плашку
 * рисует клиент, и на телефоне (P9) она обязана быть той же самой. Текст
 * приезжает с сервера готовым — в нём бывает дата норм и номер пункта, а их
 * знает только результат.
 */
export interface SafetyNotice {
  /**
   * Куда встаёт относительно ответа.
   *
   * `before` существует ради одного случая — признаков неотложного состояния:
   * docs/disclaimers.md требует сначала телефон экстренной службы, потом всё
   * остальное. Обычный дисклеймер стоит ПОД результатом и его не заменяет.
   */
  placement: 'before' | 'after'
  text: string
}

/**
 * Плашки о коде из СМС и данных карты (SMY-551). Тексты — дословно таблица
 * `docs/copy/fraud-warning.md` (ключи `chat.fraud_notice.*`, сверяет
 * `apps/api/src/ai/safety.test.ts`), когда какая встаёт — требование 16 PRD
 * каталога. Здесь, а не на сервере: по тексту клиент узнаёт вид плашки.
 */
export const CARD_CODE_TEXT = {
  before: 'Никому не передавай данные карты, CVV и коды из СМС от банка — их просят только мошенники. Уже передал — звони в банк по номеру с карты.',
  afterAnswer: 'В ответе есть строки под данные карты, CVV или код из СМС — убери их перед отправкой. Такое просят только мошенники.',
} as const

/** Вид плашки: обычная или предупреждение — цветом и значком (решение владельца 26.10.2026). */
export type NoticeTone = 'default' | 'warning'

/**
 * Вид плашки по её содержимому. По тексту, а не полем: лента хранит у плашки
 * только текст (`kind: 'notice'`), и поле вида потребовало бы миграции, а живой
 * поток и перезагрузка рисовали бы по-разному. Предупреждение — плашки о коде из
 * СМС и данных карты; красный флаг, остальные тексты и не текст (разметка
 * клиента) — прежний вид. Одно правило на оба клиента: разойдись оно копиями,
 * веб и приложение нарисовали бы одну плашку по-разному.
 *
 * Сверка — точным равенством. Поэтому ПРАВКА ТЕКСТА в `CARD_CODE_TEXT` — это и
 * прежний текст в `CARD_CODE_TEXT_BEFORE`: строки ленты хранят прежний, и без
 * него после перезагрузки они стали бы обычной плашкой, как и новые тексты в
 * приложении, собранном до правки.
 */
export const noticeTone = (content: unknown): NoticeTone =>
  typeof content === 'string' && (Object.values(CARD_CODE_TEXT) as string[]).concat(CARD_CODE_TEXT_BEFORE).includes(content)
    ? 'warning'
    : 'default'

/** Прежние редакции `CARD_CODE_TEXT`, которые лежат в лентах. Пусто: в `main` до 26.10.2026 плашек не было. */
export const CARD_CODE_TEXT_BEFORE: readonly string[] = []

/**
 * На какое число собран ответ, сходивший в интернет. SMY-47
 *
 * До 07.09.2026 это был дисклеймер с сервера — «Собрано по источникам на
 * 2026-09-07 — они могли обновиться» — и стоял плашкой ПОД ответом, рядом с
 * границами ответственности. Оттуда он ушёл: вокруг одного ответа набралось
 * четыре яруса (строка запуска, пузырь, источники, кнопки), и экран читался
 * тяжелее самого ответа. Из всей пачки эта строка единственная говорит не про
 * границу, а про сам ответ, — место ей рядом с «за сколько» и «сколько
 * списалось», то есть в подписи над ответом, справа.
 *
 * ТЕКСТ ЗДЕСЬ, А НЕ НА СЕРВЕРЕ, и это разница с `SafetyNotice`. Там в тексте
 * бывает номер пункта и дата норм — их знает только результат, и клиент их не
 * выведет. Здесь известна ровно одна дата, а слов два: сервер отвечает за
 * факт (когда ходили за источниками), клиент — за слова. В ядре они потому,
 * что клиентов будет три и разойтись им нельзя.
 *
 * «Могли обновиться» ушло вместе с длиной: дата и есть это предупреждение —
 * «на 07.09.2026» означает «не позже», и в подписи серым это читается за раз.
 */
export function sourcesNote(isoDate: string): string {
  const [year, month, day] = isoDate.slice(0, 10).split('-')
  // Дата, которую не разобрать, не повод скрыть источники: строка без числа
  // хуже строки с числом, но лучше пропавшей.
  if (year == null || month == null || day == null) return 'Источники'
  return `Источники на ${day}.${month}.${year}`
}

/**
 * Пометка ответа, собранного из прошлого поиска без нового. SMY-497 · 26.10.2026
 *
 * Встаёт в подпись над ответом на месте `sourcesNote`: сервер шлёт момент того
 * поиска (`pastSearchAt` ленты), слова — здесь, одни на все типы с поиском и на
 * карточки товаров (`assumptions.past_search` в
 * `docs/copy/household-task-catalog.md`, `cards.moment.previous` в
 * `docs/copy/product-cards.md`; сверяет `assumptions.test.ts`). Время — в поясе
 * устройства; `timeZone` — для тестов, чтобы пояс машины не прятал подмену
 * местного времени на всемирное. Момент, который не разобрать, даёт пометку без
 * числа: она нужнее точности.
 */
export function pastSearchNote(iso: string, timeZone?: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return 'Прошлый поиск'
  const zone = timeZone != null ? { timeZone } : {}
  const date = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', ...zone }).format(at)
  const time = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit', ...zone }).format(at)
  return `Прошлый поиск: ${date}, ${time}`
}

/**
 * Отказ по содержанию запроса — нашим правилом, а не фильтром поставщика.
 * SMY-78 · P7 Э7.1
 *
 * Причина названа, но названа коротко и по-человечески. Ни категории правила,
 * ни совета «переформулируй» здесь нет: подсказка, как обойти фильтр, — это и
 * есть обход фильтра. Категория живёт в журнале запуска (`blocked_reason`),
 * куда человек не смотрит.
 */
export interface Refusal {
  text: string
  /**
   * Что вместо этого сделать можно.
   *
   * Есть не у всякого отказа, и это решение, а не пробел: рядом с отказом по
   * материалам о несовершеннолетних любое «зато могу» читается как торг.
   */
  alternative?: string
}

/**
 * Чем кончился запуск без результата — заголовком и объяснением.
 *
 * Жили копией в `RunCard.tsx` веба и приложения, пока говорил один экран. С
 * 05.10.2026 то же объяснение пишет в ленту сервер (SMY-352): после
 * перезагрузки вопрос больше не висит без ответа. Три копии одной строки
 * разошлись бы на первой правке. Тексты — `docs/copy/chat-recovery.md`.
 *
 * Исходов три, и объединять их нельзя. «Не получилось» без причины оставляет
 * человека гадать, повторять ли: у сбоя ответ «да», у отказа по содержанию —
 * «нет, повтор не поможет». И в обоих случаях главное одно — списания не было.
 */
export const RUN_OUTCOME_COPY = {
  failed: {
    title: 'Не получилось',
    why: 'Задача не доехала до результата — это на нашей стороне. Попробуй запустить ещё раз, с лимита ничего не списалось.',
  },
  blocked: {
    title: 'Такую тему не беру',
    // Прежний текст — «Такую задачу я не выполняю» — звучал как каприз и не
    // отвечал на главный вопрос: почему. Человек, получивший отказ на теме
    // 18+, читал его как сбой и жал «ещё раз». Причина названа прямо: дело в
    // теме, а не в формулировке и не в нас. С 05.10.2026 — и следующий шаг
    // («другую тему возьму»), и пара глоссария «с лимита» вместо «Списания не
    // было»: без неё строка читалась «ничего не потрачено», а отказ в чате
    // бесплатное уточнение тратит (`docs/copy/chat-recovery.md`).
    why: 'На запрещённые темы — в том числе откровенные, жестокие и опасные — я не отвечаю и картинок по ним не рисую. Повтор и другая формулировка не помогут, а другую тему возьму. С лимита ничего не списалось.',
  },
  cancelled: { title: 'Отменено', why: 'Отмена до финала лимит не списывает — считается только готовый результат.' },
} as const

/**
 * Цена повтора сбоя — строкой рядом с кнопкой повтора. SMY-352, SMY-361
 *
 * Называет единицу: сколько спишется, говорят до запуска (п. 3.3 правил), а
 * строка под полем называет единицу следующего НОВОГО вопроса — у повтора
 * картинки она была бы чужой. Бесплатна строка у уточнения в оплаченном чате:
 * сбой запас не тратит, и повтор там тоже уточнение. Одна функция на карточку
 * и плашку обеих платформ: четыре копии строки разошлись, и у плашки
 * бесплатного варианта не было вовсе (ворота ревью 05.10.2026).
 */
export const retryCost = (free: boolean, unit: UnitId): string =>
  free ? 'Уточнение — без списания лимита.' : `Спишется ${formatUnit(1, unit)} с лимита — только если получится.`

/**
 * Ключ попытки старта: повтор с тем же ключом получает тот же запуск. SMY-449
 *
 * Клиент заводит ключ на каждую попытку — отправку, повтор сбоя, правку — и,
 * потеряв ответ на обрыве связи, повторяет запрос с ним же; сервер на известный
 * ключ отвечает прежним ответом, второй запуск не заводя (`runs/start-keys.ts`).
 * Образец один на сервер и клиентов: разойдись он с генератором, каждый старт
 * получал бы отказ.
 *
 * Не секрет: ключ значит что-то только в паре с сеансом своего человека. Поэтому
 * без нового пакета — `crypto.getRandomValues`, где он есть (веб), иначе
 * `Math.random` (в приложении на Hermes `crypto` нет, полифила тоже). Длина
 * случайной части постоянная: строка из `Math.random().toString(36)` бывает
 * короче семи знаков.
 *
 * Спереди — время в миллисекундах. Насколько засеян генератор Hermes, мы не
 * знаем, а совпадение ключа у одного человека отдало бы ему старый запуск
 * вместо нового — и навсегда: в базе ключ бессрочный. Одинаково засеянный
 * генератор в двух запусках приложения дал бы одну и ту же последовательность;
 * время их разводит (ворота ревью 06.10.2026, security и test-auditor).
 */
export const ATTEMPT_KEY = /^[0-9A-Za-z_-]{16,64}$/

const KEY_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz'
const KEY_LENGTH = 24

export function newAttemptKey(): string {
  const bytes = new Uint8Array(KEY_LENGTH)
  const crypto = (globalThis as { crypto?: { getRandomValues?: (into: Uint8Array) => Uint8Array } }).crypto
  if (crypto?.getRandomValues != null) crypto.getRandomValues(bytes)
  else for (let i = 0; i < KEY_LENGTH; i++) bytes[i] = Math.floor(Math.random() * 256)
  let key = Date.now().toString(36)
  for (const byte of bytes) key += KEY_ALPHABET.charAt(byte % KEY_ALPHABET.length)
  return key
}

/**
 * Старт или правка остались без ответа, и дошли ли они до сервера, неизвестно.
 * SMY-352, SMY-449
 *
 * Не «Нет связи. Проверь интернет.»: та подталкивает нажать ещё раз, а новое
 * нажатие — новая попытка с новым ключом и, дойди первая, второй запуск. Экран
 * закрывает поле и повторы и сам дошлёт ту же попытку тем же ключом — сервер
 * отдаст тот же запуск. Отсюда обещание «дважды не запущу» — и только при
 * условии «оставайся здесь»: попытка живёт, пока жив экран, — уход ставит её на
 * паузу у её чата, а попытку нового чата снимает. Строк две, по поводу
 * (решение владельца 06.10.2026, «своя строка»): обрыв связи — «Нет связи»,
 * чтобы человек проверил сеть; 502 на выкладке и ответ, не пришедший за срок, —
 * «Сервер не ответил», одними словами с `api.error.unavailable`. По платформе,
 * как `REQUEST_REJECTED`: в вебе попытку теряет и перезагрузка вкладки
 * (`docs/copy/chat-recovery.md`, `chat.start.unsure_*` и `chat.start.offline_*`).
 */
export const START_UNSURE = {
  web: {
    server: 'Сервер не ответил. Оставайся здесь и не обновляй страницу — тогда дошлю сам, как только он ответит, и дважды не запущу.',
    offline: 'Нет связи. Оставайся здесь и не обновляй страницу — тогда дошлю сам, как только связь появится, и дважды не запущу.',
  },
  app: {
    server: 'Сервер не ответил. Оставайся здесь — тогда дошлю сам, как только он ответит, и дважды не запущу.',
    offline: 'Нет связи. Оставайся здесь — тогда дошлю сам, как только связь появится, и дважды не запущу.',
  },
} as const

/**
 * Отказ нашим правилом — его словами, с заменой и главным: с лимита ничего не
 * списалось. SMY-78
 *
 * Про списание сказать надо всегда: молчание об этом человек прочтёт как
 * «списали». «С лимита», а не «Списания не было» — пара глоссария: отказ в
 * чате бесплатное уточнение тратит, и «ничего не списалось» без «с лимита»
 * обещало бы больше, чем правда.
 */
export const refusalWhy = (refusal: Refusal): string =>
  [refusal.text, refusal.alternative, 'С лимита ничего не списалось.'].filter(Boolean).join(' ')

/**
 * Что лежит в ленте на месте ответа, которого нет. SMY-352
 *
 * Пишет сервер — по финалу запуска и в разборе осиротевших после рестарта, —
 * а показывают оба клиента. `null` у готового: ответ пишет он сам.
 *
 * У отмены строка своя, а не `RUN_OUTCOME_COPY.cancelled.why`: на карточке над
 * объяснением стоит заголовок «Отменено», а в ленте его нет, и правило «отмена
 * лимит не списывает» читалось бы правилом, а не тем, что случилось с задачей.
 */
export function outcomeNote(outcome: 'done' | 'failed' | 'blocked' | 'cancelled', refusal?: Refusal): string | null {
  if (outcome === 'done') return null
  if (outcome === 'blocked') return refusal != null ? refusalWhy(refusal) : RUN_OUTCOME_COPY.blocked.why
  if (outcome === 'cancelled') return 'Задача отменена до результата — с лимита ничего не списалось.'
  return RUN_OUTCOME_COPY.failed.why
}

export interface Run {
  id: string
  status: RunStatus
  /**
   * Сценарий, которым запущено. Экран 05 называет им заголовок и иконку.
   *
   * Хранится, а не выводится из единицы: соответствие не взаимно однозначно —
   * документами списываются и разбор, и создание, а вопросами и чат, и
   * написание текста. Догадка на экране показала бы разбор там, где человек
   * просил создать.
   */
  scenario: ScenarioId
  /** Единица, которая спишется при успешном завершении. */
  unit: UnitId
  steps: RunStep[]
  sources: Source[]
  startedAt?: string
  /** Экран 04 обещает время до результата — «примерно 40 секунд». */
  etaSeconds?: number
}

/** Терминальные состояния: списание и правки считаются только от них. */
export const isTerminal = (status: RunStatus): boolean => status === 'done' || status === 'cancelled'

/**
 * Единица списывается только за завершённый результат.
 * Отменённый и упавший запуск не стоит пользователю ничего — правило из P5.
 */
export const shouldCharge = (run: Pick<Run, 'status'>): boolean => run.status === 'done'
